import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DataGrid, Column, FilterRow, Scrolling, SearchPanel, Button as GridButton } from 'devextreme-react/data-grid';

import { Button as StandardButton } from 'devextreme-react';

import ProcessingResult from '../../framework/ProcessingResult';

import CustomStore from 'devextreme/data/custom_store';
import GlobalLoader from '../loader/GlobalLoader';

import { formatMessage } from 'devextreme/localization';

import DataSourceForm, { DataSourceFormHandle } from '../data-source-form/DataSourceForm';
import DeleteConfirmationForm from '../delete-confirmation-form/DeleteConfirmationForm';
import { DeleteResult, useDeleteConfirmation } from '../delete-confirmation-form/useDeleteConfirmation';

import MessageForm, { MessageFormHandle, WarningMessage } from '../../components/message-form/MessageForm';

import DataSourceApi from '../../api/DataSourceApi';

import DataSourceListDto from "../../classes/dtos/datasourcemodule/DataSourceListDto";
import DataSourceUsageDto from '../../classes/dtos/datasourcemodule/DataSourceUsageDto';
import GetDataSourcesResult from '../../classes/api/result/datasourcemodule/GetDataSourcesResult';


interface DataSourceGridProps {
}

const DataSourceGrid: React.FC<DataSourceGridProps> = () => {

    const dataSourceFormHandleRef = useRef<DataSourceFormHandle>(null);

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const [dataSources, setDataSources] = useState<Array<DataSourceListDto> | null>(null);

    const deleteItem = useCallback(async (item: DataSourceListDto): Promise<DeleteResult<DataSourceUsageDto[]>> => {

        let result = await DataSourceApi.DeleteDataSourceAsync(item.Id);

        if (result.IsFailed()) {
            return {
                success: false,
                message: result.ErrorMessage,
                result: null
            };
        }

        return {
            success: result.Result.Deleted,
            message: "",
            result: result.Result.UsageDetails
        }
    }, []);

    const onDeleteTerminated = useCallback(async (item: DataSourceListDto, deleteResult: DeleteResult<DataSourceUsageDto[]>): Promise<void> => {
        if (!deleteResult.success) {
            if (messageFormHandleRef.current === null) {
                alert("ERROR");
                return;
            }

            messageFormHandleRef.current.OpenComplex(CustomList(deleteResult.result ?? []), WarningMessage);
        } else {
            setDataSources(null);
        }
    }, []);

    const deleteConfirmation = useDeleteConfirmation<DataSourceListDto, DataSourceUsageDto[]>(deleteItem, onDeleteTerminated);

    const CustomList = (usages: DataSourceUsageDto[]) => {
        const maxDisplay = 20;
        const displayedUsages = usages.slice(0, maxDisplay);
        const remainingCount = usages.length - maxDisplay;

        return (
            <div>
                {formatMessage("SettingsDataSources_DataSourceIsUsed")}
                <ul>
                    {displayedUsages.map((e, index) => (
                        <li key={index}>{e.PathName} ({e.PathType})</li>
                    ))}
                </ul>
                {remainingCount === 1 && (
                    <div>
                        {formatMessage("SettingsDataSources_And1Other")}
                    </div>
                )}
                {remainingCount > 1 && (
                    <div>
                        {formatMessage("SettingsDataSources_AndXOthers", remainingCount.toString())}
                    </div>
                )}
            </div>
        );
    };

    function handleDataSourceAdded(dataSourceId: number) {
        setDataSources(null);
    }

    const refresh = useCallback(() => {
        if (!dataSources) {
            DataSourceApi.GetDataSourcesByCustomerAsync(null, null).then((allDataSources: ProcessingResult<GetDataSourcesResult>) => {
                if (allDataSources.IsFailed()) {
                    // TODO: Handle failure case (e.g., logout)
                } else {
                    setDataSources(allDataSources.Result.DataSources);
                }
            });
        }
    }, [dataSources]);

    // UseEffect to refresh when component mounts
    useEffect(() => {
        refresh();
    }, [refresh]);

    function createDataSource(): void {
        if (dataSourceFormHandleRef.current !== null) {
            dataSourceFormHandleRef.current.Open(null);
        }
    };

    function updateDataSource(dataSourceId: number): void {
        if (dataSourceFormHandleRef.current !== null) {
            dataSourceFormHandleRef.current.Open(dataSourceId);
        }
    };

    async function deleteDataSource(dataSource: DataSourceListDto): Promise<void> {
        deleteConfirmation.showConfirm(dataSource);
    }

    const customDataSource = new CustomStore({
        key: 'Id',
        load: () => Promise.resolve(dataSources ?? [])
    });

    if (!dataSources) {
        return <GlobalLoader />
    }

    return (
        <div className={"block-card"}>
            <div className={"block-card__header"}>
                <h2>{formatMessage("SettingsDataSources_Title")}</h2>
                <StandardButton icon="plus" width={140} text={formatMessage("SettingsDataSources_ButtonCreate")} type="default" stylingMode="contained" onClick={createDataSource} disabled={false} />
            </div>
            <DataGrid
                id="dataGrid"
                dataSource={customDataSource}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={false}
                hoverStateEnabled={true}
                repaintChangesOnly={true}
                height='calc(100vh - 400px)'
                onRowUpdated={(e) => {
                    e.component.refresh();
                }}>
                <Scrolling mode="infinite" />
                <FilterRow visible={true} />
                <SearchPanel visible={false} />
                <Column caption={formatMessage("SettingsDataSources_DataSourceName")} dataField="Name" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption={formatMessage("SettingsDataSources_DataSourceCreatedBy")} dataField="CreatedBy.Label" dataType="string" allowSorting={true} allowEditing={false} />
                <Column caption={formatMessage("SettingsDataSources_DataSourceType")} dataField="Type.Name" dataType="string" allowSorting={true} allowEditing={false} />
                <Column type="buttons">
                    <GridButton icon="edit" hint={formatMessage("SettingsDataSources_ButtonEdit")} onClick={(e) => { updateDataSource(e.row?.data.Id) }} />
                    <GridButton icon="trash" hint={formatMessage("SettingsDataSources_ButtonDelete")} onClick={(e) => { deleteDataSource(e.row?.data) }} />
                </Column>

            </DataGrid>
            <DataSourceForm ref={dataSourceFormHandleRef} Direction="All" DataSourceAdded={handleDataSourceAdded} />

            <DeleteConfirmationForm
                visible={deleteConfirmation.isVisible}
                onConfirm={deleteConfirmation.handleConfirm}
                onCancel={deleteConfirmation.handleCancel}
                message={formatMessage("SettingsDataSources_DeleteConfirmation", deleteConfirmation.itemToDelete?.Name ?? "")}
            />
            <MessageForm ref={messageFormHandleRef} />
        </div>
    );
};

export default DataSourceGrid;
